import { Globe, MapPin } from 'lucide-react';
import type { GeoCrossRefResult } from '../../../../api/widgetTelemetry';
import StatCard from '../shared/StatCard';

interface GeoCrossRefProps {
  data: GeoCrossRefResult | null;
  loading: boolean;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

function usd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export default function GeoCrossRef({ data, loading }: GeoCrossRefProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-24"
          />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const totalWidgetSessions = data.widgetByState.reduce(
    (sum, r) => sum + r.sessionsWidgetLoaded,
    0,
  );
  const totalPurchases = data.widgetByState.reduce((sum, r) => sum + r.sessionsPurchase, 0);
  const totalRevenue = data.bookingsByRegion.reduce((sum, r) => sum + r.bookingRevenue, 0);
  const totalBookings = data.bookingsByRegion.reduce((sum, r) => sum + r.totalBookings, 0);

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
        <p className="text-xs text-amber-700 dark:text-amber-400">
          <strong>Why two tables?</strong> Widget sessions are grouped by IP-derived
          state code; warehouse bookings are grouped by BonsaiBar region. The two
          aggregations don&apos;t share a key, so we show them side-by-side — useful
          for spotting widget traffic that isn&apos;t converting in a given region.
          For session-level attribution, see the Revenue Reconciliation tab.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label="Widget Sessions (top 30 states)"
          value={totalWidgetSessions.toLocaleString()}
          icon={Globe}
          iconColor="text-water-600"
        />
        <StatCard
          label="Widget Purchases"
          value={totalPurchases.toLocaleString()}
          icon={Globe}
          iconColor="text-moss-600"
        />
        <StatCard
          label="Warehouse Bookings / Revenue"
          value={`${totalBookings.toLocaleString()} / ${usd(totalRevenue)}`}
          icon={MapPin}
          iconColor="text-amber-600"
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700">
            <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100">
              Widget Sessions by State (GA4)
            </h3>
            <p className="text-xs text-stone-400 mt-1">Top 30 states by widget_loaded sessions</p>
          </div>
          {data.widgetByState.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-8">No widget data for this range</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 dark:bg-[#1e2520]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">State</th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Loaded</th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Selected</th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Purchased</th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Conv %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
                {data.widgetByState.map((row) => (
                  <tr key={row.ipRegionCode} className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]">
                    <td className="px-4 py-2 text-bark-900 dark:text-stone-200 font-medium">
                      {row.ipRegionCode}
                    </td>
                    <td className="px-4 py-2 text-right">{row.sessionsWidgetLoaded.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{row.sessionsWorkshopSelected.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{row.sessionsPurchase.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{pct(row.conversionRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
          <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700">
            <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100">
              Bookings by BB Region (Warehouse)
            </h3>
            <p className="text-xs text-stone-400 mt-1">
              From <code>gold.agg_regional_marketing_summary</code>
            </p>
          </div>
          {data.bookingsByRegion.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-8">No booking data for this range</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 dark:bg-[#1e2520]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">BB Region</th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Bookings</th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Revenue</th>
                  <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Avg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
                {data.bookingsByRegion.map((row) => (
                  <tr
                    key={`${row.regionKey ?? 'x'}-${row.bonsaibarRegion}`}
                    className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]"
                  >
                    <td className="px-4 py-2 text-bark-900 dark:text-stone-200 font-medium">
                      {row.bonsaibarRegion}
                    </td>
                    <td className="px-4 py-2 text-right">{row.totalBookings.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{usd(row.bookingRevenue)}</td>
                    <td className="px-4 py-2 text-right">{usd(row.avgBookingValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
